import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Lock, LockOpen, Eye, EyeOff, Loader2 } from "lucide-react";
import * as api from "@/lib/api";

const GOLD = "hsl(43 74% 58%)";

export default function PrivateLock({ workspace }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isPrivate = workspace === "private";

  useEffect(() => {
    if (!isPrivate && params.get("unlock") === "1") {
      setOpen(true);
      params.delete("unlock");
      setParams(params, { replace: true });
    }
  }, [isPrivate, params, setParams]);

  const onClick = async () => {
    if (!isPrivate) { setPw(""); setError(""); setShow(false); setOpen(true); return; }
    try { await api.lockPrivate(); } catch (e) { console.error("Lock:", e); }
    api.setWorkspace("main");
    navigate("/", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!pw) { setError("Inserisci la password."); return; }
    setBusy(true); setError("");
    try {
      await api.unlockPrivate(pw);
      setOpen(false); setPw("");
      navigate("/privato", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Password errata.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={onClick} title={isPrivate ? "Area privata — clicca per uscire" : "Accedi all'area privata"}
        className="grid h-8 w-8 place-items-center rounded-lg border transition-colors"
        style={isPrivate ? { borderColor: GOLD, color: GOLD, background: "hsl(43 74% 58% / 0.12)" } : { borderColor: "rgba(255,255,255,0.1)" }}
        data-testid="private-lock-btn" data-state={isPrivate ? "open" : "closed"}>
        {isPrivate ? <LockOpen className="h-4 w-4" /> : <Lock className="h-4 w-4 text-muted-foreground" />}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-white/10 bg-[hsl(var(--card))] text-foreground sm:max-w-sm" data-testid="private-unlock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display"><Lock className="h-4 w-4" style={{ color: GOLD }} /> Area privata</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Inserisci la password per accedere all'area riservata.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="relative">
              <input type={show ? "text" : "password"} autoFocus autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)}
                placeholder="Password" className="lab-input pr-10" data-testid="private-password-input" />
              <button type="button" onClick={() => setShow((v) => !v)} title={show ? "Nascondi" : "Mostra"} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground" data-testid="private-password-toggle">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {error && <div className="rounded-lg border border-[hsl(0_72%_60%)]/40 bg-[hsl(0_72%_60%)]/10 px-3 py-2 text-xs text-[hsl(0_72%_72%)]" data-testid="private-unlock-error">{error}</div>}
            <button type="submit" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-black transition-transform hover:-translate-y-0.5 disabled:opacity-60" style={{ background: GOLD }} data-testid="private-unlock-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LockOpen className="h-4 w-4" />} Accedi
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
